import axios from "axios";
import { TILESAPI_BASE_URL } from "utils/base_urls";

const singleton = Symbol();
const singletonEnforcer = Symbol();

class AdminInterface {
    constructor() {
        this.session = axios.create({
            baseURL: TILESAPI_BASE_URL,
            headers: {
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
                'Authorization': `Bearer ${localStorage.getItem("tiles_api_auth0_token")}`
            }
        });
    }

    static get instance() {
        if (!this[singleton]) {
            this[singleton] = new AdminInterface(singletonEnforcer);
        }

        return this[singleton];
    }

    getUsers = (page, per_page) => {
        if (page && per_page) return this.session.get(`v1/management/users?page=${page}&per_page=${per_page}`)
        else return this.session.get('v1/management/users')
    }
    createUser = (user) => this.session.post('v1/management/users', user)
    getUser = (user_id) => this.session.get(`v1/management/users/${user_id}`)
    deleteUser = (user_id) => this.session.delete(`v1/management/users/${user_id}`)
    updateUser = (user_id, user) => this.session.put(`v1/management/users/${user_id}`, user)

    getOrganizations = () => this.session.get('v1/management/organizations')
    createOrganization = (organization) => this.session.post('v1/management/organizations', organization)
    getOrganization = (organization_id) => this.session.get(`v1/management/organizations/${organization_id}`)
    deleteOrganization = (organization_id) => this.session.delete(`v1/management/organizations/${organization_id}`)
    updateOrganization = (organization_id, organization) => this.session.put(`v1/management/organizations/${organization_id}`, organization)

    getSubscriptions = () => this.session.get('v1/management/organizations/subscriptions/')
    getSubscription = (organization_id) => this.session.get(`v1/management/organizations/${organization_id}/subscriptions/`)
    updateSubscription = (subscription_id, subscription) => this.session.put(`v1/management/organizations/subscriptions/${subscription_id}`, subscription)

    getSubscriptionPrices = () => this.session.get('v1/management/plans')
    updateSubscriptionPrice = (subscription_price_id, subscriptionPrice) => this.session.put(`v1/management/plans/${subscription_price_id}/price`, subscriptionPrice)

    getCropRequests = () => this.session.get(`v1/management/exports/crops`)
    getCropRequestPrices = () => this.session.get(`v1/management/exports/crops/prices`)
    updateCropRequestPrice = (cropRequestPrice) => this.session.put(`v1/management/exports/crops/prices`, cropRequestPrice)
    createCropRequestPrice = (cropRequestPrice) => this.session.post(`v1/management/exports/crops/prices`, cropRequestPrice)
    deleteCropRequestPrice = (collection_id, resolution) => this.session.delete(`v1/management/exports/crops/prices?collection_id=${collection_id}&resolution=${resolution}`)

    getPrintRequests = () => this.session.get(`v1/management/exports/prints`)

    getServiceUsages = () => this.session.get(`v1/management/services/usages`)
    getServiceUsageByLayer = () => this.session.get(`v1/management/services/usages/by_layer`)
    getServiceUsageByService = () => this.session.get(`v1/management/services/usages/by_service`)
    getServiceUsageByCollection = () => this.session.get(`v1/management/services/usages/by_collection`)
    getUserServiceUsageByLayer = (user_id) => this.session.get(`v1/management/services/usages/${user_id}/by_layer`)
    getUserServiceUsageForService = (service, user_id) => this.session.get(`v1/management/services/${service}/usages/${user_id}`)
    getDailyUsage = () => this.session.get(`v1/management/services/usages/daily`)
    getHourlyUsage = () => this.session.get(`v1/management/services/usages/hourly`)
    getElastiCacheStoredData = () => this.session.get(`v1/management/aws/elasticache`)

    getProviders = () => this.session.get(`v1/management/providers`)
    createProvider = (provider) => this.session.post(`v1/management/providers`, provider)
    deleteProvider = (provider_id) => this.session.delete(`v1/management/providers/${provider_id}`)
    updateProvider = (provider) => this.session.put(`v1/management/providers/${provider.id}`, provider)

    getLicenses = () => this.session.get(`v1/management/licenses`)
    createLicense = (license) => this.session.post(`v1/management/licenses`, license)
    deleteLicense = (license_id) => this.session.delete(`v1/management/licenses/${license_id}`)
    updateLicense = (license) => this.session.put(`v1/management/licenses/${license.id}`, license)
    getLicenseUsers = (license_id) => this.session.get(`v1/management/licenses/${license_id}/users`)
    deleteLicenseUser = (license_id, user_id) => this.session.delete(`v1/management/licenses/${license_id}/users/${user_id}`)
    createLicenseUser = (licenseUser) => this.session.post(`v1/management/licenses/${licenseUser.license_id}/users`, licenseUser)

    getInstruments = () => this.session.get(`v1/management/instruments`)
    createInstrument = (instruments) => this.session.post(`v1/management/instruments`, instruments)
    deleteInstrument = (instruments_id) => this.session.delete(`v1/management/instruments/${instruments_id}`)
    updateInstrument = (instruments) => this.session.put(`v1/management/instruments/${instruments.id}`, instruments)

    getCalibrations = () => this.session.get(`v1/management/calibrations`)
    createCalibration = (calibration) => this.session.post(`v1/management/calibrations`, calibration)
    deleteCalibration = (calibration_id) => this.session.delete(`v1/management/calibrations/${calibration_id}`)
    updateCalibration = (calibration) => this.session.put(`v1/management/calibrations/${calibration.id}`, calibration)

    getNumberOfAssets = () => this.session.get(`v1/management/assets/count`)
    getNumberOfAssetsInMission = (mission_id) => this.session.get(`v1/management/assets/mission/${mission_id}/count`)
    getAssetsInMission = (mission_id, skip, limit) => this.session.get(`v1/management/assets/mission/${mission_id}?skip=${skip}&limit=${limit}`)
    deleteAsset = (uuid) => this.session.delete(`v1/management/assets/${uuid}`)
    getAssetStac = (uuid) => this.session.get(`v1/management/assets/${uuid}/stac`)
    updateAsset = (uuid, asset) => this.session.put(`v1/management/assets/${uuid}`, asset)
    updateAssetStac = (uuid, stac) => this.session.put(`v1/management/assets/${uuid}/stac`, stac)
    getCreateAssetRequests = () => this.session.get(`v1/management/assets/create_asset_requests`)
    editCreateAssetRequest = (id, createAssetRequest) => this.session.put(`v1/management/assets/create_asset_requests/${id}`, createAssetRequest)
    deleteCreateAssetRequest = (id) => this.session.delete(`v1/management/assets/create_asset_requests/${id}`)
    retryCreateAssetRequests = (id) => this.session.post(`v1/management/assets/create_asset_requests/${id}/retry`)
    createWebServiceAsset = (asset) => this.session.post(`v1/management/assets/create_asset_requests/web_service`, asset)
    createStreetviewAssets = (asset) => this.session.post(`v1/management/assets/create_asset_requests/streetview`, asset)
    createMosaicAsset = (asset) => this.session.post(`v1/management/assets/create_asset_requests/mosaic/from_directory`, asset)
    createTilesetAssets = (asset) => this.session.post(`v1/management/assets/create_asset_requests/tileset/from_directory`, asset)
    createObliqueTilesetAssets = (asset) => this.session.post(`v1/management/assets/create_asset_requests/tileset/oblique/from_directory`, asset)

    getCollections = () => this.session.get(`v1/management/collections`)
    getCollection = (id) => this.session.get(`v1/management/collections/${id}`)
    deleteCollection = (id) => this.session.delete(`v1/management/collections/${id}`)
    createCollection = (collection) => this.session.post(`v1/management/collections`, collection)
    updateCollection = (collection) => this.session.put(`v1/management/collections/${collection.id}`, collection)

    getMissions = () => this.session.get(`v1/management/missions`)
    getMission = (id) => this.session.get(`v1/management/missions/${id}`)
    deleteMission = (id) => this.session.delete(`v1/management/missions/${id}`)
    deleteMissionAssets = (id) => this.session.delete(`v1/management/missions/${id}/assets`)
    createMission = (mission) => this.session.post(`v1/management/missions`, mission)
    updateMission = (mission) => this.session.put(`v1/management/missions/${mission.id}`, mission)
    downloadMissionCentroids = (id) => this.session.get(`v1/management/missions/${id}/centroids`)
    createMissionProvider = (missionProvider) => this.session.post(`v1/management/missions/${missionProvider.missionId}/providers`, missionProvider)
    createMissionInstrument = (missionInstrument) => this.session.post(`v1/management/missions/${missionInstrument.missionId}/instruments`, missionInstrument)
    deleteMissionInstrument = (missionInstrument) => this.session.delete(`v1/management/missions/${missionInstrument.missionId}/instruments/${missionInstrument.instrumentId}`)
    deleteMissionProvider = (missionProvider) => this.session.delete(`v1/management/missions/${missionProvider.missionId}/providers/${missionProvider.providerId}/${missionProvider.role}`)
    
    getFiles = () => this.session.get(`v1/management/files`);
    getFile = (id) => this.session.get(`v1/management/files/${id}`);
    deleteFile = (id) => this.session.delete(`v1/management/files/${id}`)
    getFilesUsage = () => this.session.get(`v1/management/files/usage`)
    downloadFile = (id) => this.session.get(`v1/management/files/${id}/download`)
    initUpload = (uploadFileData) => this.session.post(`v1/management/files/upload/initialize`, uploadFileData)
    signUploadParts = (upload_id, part_numbers) => this.session.post('v1/management/files/upload/parts/sign', { upload_id: upload_id, part_numbers: part_numbers, expires_in_seconds: 900 })
    upload = (upload_url, file_chunk) => this.session.put(upload_url, file_chunk)
    abortUpload = (upload_id) => this.session.post(`v1/management/files/upload/abort`, { upload_id: upload_id })
    completeUpload = (upload_id, parts) => this.session.post(`v1/management/files/upload/complete`, { upload_id: upload_id, parts: parts });

    getCountries = () => this.session.get(`v1/management/countries`)
    getStates = (country_id) => this.session.get(`v1/management/countries/${country_id}/states`)
    getCities = (country_id, state_id) => this.session.get(`v1/management/countries/${country_id}/states/${state_id}/cities`)
}

export default AdminInterface